import React, { useMemo } from 'react'
import { Button } from '../../../components/Button/Button'
import { ReactComponent as ZocksLogo } from '../../../assets/ZocksLogo.svg'
import { ReactComponent as BulkStepIcon } from '../../../assets/images/cross-client-match.svg'
import { ReactComponent as PotentialMatchIcon } from '../../../assets/images/cross-client-potential-match.svg'
import { ReactComponent as NoMatchIcon } from '../../../assets/images/cross-client-no-match.svg'
import { type CrossClientQueryAtomicClause } from '../../../models/CrossClient'
import { getIntegrationSourceDisplayNames, joinNames, pluralize } from '../utils'
import { type CrossClientReviewState } from './hooks'

interface CrossClientChatReviewProps {
  reviewState: CrossClientReviewState
}

type TimelineItem =
  | { kind: 'lead'; key: string; content: React.ReactNode }
  | { kind: 'criteria'; key: string; clauses: CrossClientQueryAtomicClause[] }
  | { kind: 'working'; key: string; text: string }
  | { kind: 'note'; key: string; content: React.ReactNode }
  | { kind: 'result'; key: string; icon: 'match' | 'no-match' | 'potential'; title: React.ReactNode; description?: React.ReactNode }

export const CrossClientChatReview: React.FC<CrossClientChatReviewProps> = ({ reviewState }) => {
  const items = useMemo(() => buildTimeline(reviewState), [reviewState])

  return (
    <div className='d-flex flex-column gap-3 cross-client-breakdown'>
      {items.map((item) => (
        <TimelineRow key={item.key} item={item} />
      ))}
    </div>
  )
}

interface CrossClientChatReviewActionsProps {
  reviewState: CrossClientReviewState
  onViewResults: () => void
  onRestart: () => void
  onStart: () => void
  onCancel: () => void
  isLoading: boolean
}

export const CrossClientChatReviewActions: React.FC<CrossClientChatReviewActionsProps> = ({
  reviewState,
  onViewResults,
  onRestart,
  onStart,
  onCancel,
  isLoading,
}) => {
  if (reviewState.kind === 'initializing' || reviewState.kind === 'bulk-running') {
    return null
  }
  if (reviewState.kind === 'preview') {
    return (
      <div className='d-flex gap-2'>
        <Button variant='header' className='button-medium cross-client-chat-action' onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant='primary' className='button-medium cross-client-chat-action' onClick={onStart} loading={isLoading}>
          Start query
        </Button>
      </div>
    )
  }
  if (reviewState.kind === 'rejected') {
    return (
      <div className='d-flex gap-2'>
        <Button variant='primary' className='button-medium cross-client-chat-action' onClick={onRestart}>
          Ask another question
        </Button>
      </div>
    )
  }
  const isContinue =
    reviewState.kind === 'no-bulk' ||
    (reviewState.kind === 'bulk-done' && reviewState.resultSummary.fullCount === 0 && reviewState.resultSummary.potentialCount === 0)
  return (
    <div className='d-flex gap-2'>
      <Button variant='header' className='button-medium cross-client-chat-action' onClick={onRestart} disabled={isLoading}>
        Refine question
      </Button>
      <Button variant='primary' className='button-medium cross-client-chat-action' onClick={onViewResults} loading={isLoading}>
        {isContinue ? 'Continue' : 'View results'}
      </Button>
    </div>
  )
}

const ZocksBulletLogo: React.FC = () => (
  <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-message-logo zocks-border-0.25 cross-client-breakdown__bullet--zocks'>
    <ZocksLogo width={12} height={12} />
  </div>
)

const TimelineRow: React.FC<{ item: TimelineItem }> = ({ item }) => {
  switch (item.kind) {
    case 'lead':
      return (
        <div className='position-relative d-flex align-items-start zocks-gap-0.75 cross-client-breakdown__step'>
          <ZocksBulletLogo />
          <div className='paragraph-medium mb-0'>{item.content}</div>
        </div>
      )
    case 'criteria':
      return (
        <ul className='position-relative d-flex flex-column gap-3 cross-client-breakdown__criteria'>
          {item.clauses.map((clause) => (
            <li key={clause.variable} className='d-flex align-items-center gap-2 cross-client-breakdown__criterion'>
              <i className='bi bi-x-diamond-fill cross-client-breakdown__criterion-icon' />
              <span>{clause.questionForm || clause.description}</span>
            </li>
          ))}
        </ul>
      )
    case 'working':
      return (
        <div className='position-relative d-flex align-items-center zocks-gap-0.75 cross-client-breakdown__step'>
          <ZocksBulletLogo />
          <p className='paragraph-medium mb-0 shimmering-loader-text'>{item.text}</p>
        </div>
      )
    case 'note':
      return <div className='paragraph-medium mb-0 cross-client-breakdown__note'>{item.content}</div>
    case 'result':
      return (
        <div className='position-relative d-flex align-items-start zocks-gap-0.75 cross-client-breakdown__step'>
          {item.icon === 'no-match' ? (
            <NoMatchIcon width={16} height={16} className='cross-client-breakdown__step-icon' />
          ) : item.icon === 'potential' ? (
            <PotentialMatchIcon width={16} height={16} className='cross-client-breakdown__step-icon' />
          ) : (
            <BulkStepIcon width={16} height={16} className='cross-client-breakdown__step-icon' />
          )}
          <div className='d-flex flex-column gap-1'>
            <div className='paragraph-medium mb-0'>{item.title}</div>
            {item.description ? <div className='paragraph-medium mb-0 zocks-text-gray-700'>{item.description}</div> : null}
          </div>
        </div>
      )
  }
}

const buildTimeline = (state: CrossClientReviewState): TimelineItem[] => {
  const clauses = state.atomicClauses
  const count = clauses.length
  const noun = pluralize(count, 'criterion', 'criteria')

  const introContent = (
    <>
      <strong>{count}</strong> {noun} found.
    </>
  )

  switch (state.kind) {
    case 'rejected':
      return [{ kind: 'lead', key: 'rejected:lead', content: state.message }]
    case 'preview':
      return [
        { kind: 'lead', key: 'preview:lead', content: introContent },
        { kind: 'criteria', key: 'preview:criteria', clauses },
        { kind: 'note', key: 'preview:note', content: 'Start the query to check these criteria against your clients.' },
      ]
    case 'initializing':
      return [
        { kind: 'lead', key: 'initializing:lead', content: introContent },
        { kind: 'criteria', key: 'initializing:criteria', clauses },
        { kind: 'working', key: 'initializing:working', text: 'Checking Client Profiles in Zocks' },
      ]
    case 'bulk-running':
      return [
        { kind: 'lead', key: 'bulk-running:lead', content: introContent },
        { kind: 'criteria', key: 'bulk-running:criteria', clauses },
        { kind: 'working', key: 'bulk-running:working', text: 'Checking Client Profiles in Zocks' },
      ]
    case 'bulk-done': {
      const { resultSummary } = state
      const matchedCount = resultSummary.fullCount
      const potentialCount = resultSummary.potentialCount

      if (matchedCount === 0 && potentialCount === 0 && !state.hasLocal) {
        return [
          { kind: 'lead', key: 'bulk-done:intro', content: introContent },
          { kind: 'criteria', key: 'bulk-done:criteria', clauses },
          {
            kind: 'result',
            key: 'bulk-done:no-match',
            icon: 'no-match',
            title: (
              <>
                Client Profiles in Zocks <strong>didn't confirm any matches</strong>. You can analyze clients further from the results page.
              </>
            ),
          },
        ]
      }

      if (matchedCount === 0 && potentialCount === 0 && state.hasLocal) {
        const integrationsList = joinNames(getIntegrationSourceDisplayNames(state.sources))
        return [
          { kind: 'lead', key: 'bulk-done:intro', content: introContent },
          { kind: 'criteria', key: 'bulk-done:criteria', clauses },
          {
            kind: 'result',
            key: 'bulk-done:no-match',
            icon: 'no-match',
            title: (
              <>
                Client Profiles in Zocks <strong>didn't confirm any matches</strong>. I'll pull richer records from{' '}
                {integrationsList || 'your connected sources'}. They may reveal clients that qualify.
              </>
            ),
          },
        ]
      }

      const summaryItems: TimelineItem[] = []
      if (matchedCount > 0) {
        summaryItems.push({
          kind: 'result',
          key: 'bulk-done:matched',
          icon: 'match',
          title: (
            <>
              <strong>
                {matchedCount.toLocaleString()} {pluralize(matchedCount, 'match', 'matches')}:
              </strong>{' '}
              these clients were confirmed based on the data collected so far.
            </>
          ),
        })
      }
      if (potentialCount > 0) {
        summaryItems.push({
          kind: 'result',
          key: 'bulk-done:potential',
          icon: 'potential',
          title: (
            <>
              <strong>
                {potentialCount.toLocaleString()} potential {pluralize(potentialCount, 'match', 'matches')}:
              </strong>{' '}
              these clients may qualify but couldn&apos;t be confirmed yet.
            </>
          ),
        })
      }
      return [
        { kind: 'lead', key: 'bulk-done:intro', content: introContent },
        { kind: 'criteria', key: 'bulk-done:criteria', clauses },
        { kind: 'note', key: 'bulk-done:found', content: "Here's what I found in Client Profiles in Zocks:" },
        ...summaryItems,
        {
          kind: 'note',
          key: 'bulk-done:closing',
          content: 'Head to the results to review your matches and decide how to proceed with potential and remaining clients.',
        },
      ]
    }
    case 'no-bulk':
      if (state.hasLocal) {
        const integrationsList = joinNames(getIntegrationSourceDisplayNames(state.sources))
        return [
          { kind: 'lead', key: 'no-bulk:intro', content: introContent },
          { kind: 'criteria', key: 'no-bulk:criteria', clauses },
          {
            kind: 'result',
            key: 'no-bulk:no-match',
            icon: 'no-match',
            title: (
              <>
                <strong>None of these criteria can be checked</strong> using Client Profiles in Zocks. I'll pull richer records from{' '}
                {integrationsList || 'your connected sources'}. They may reveal clients that qualify.
              </>
            ),
          },
        ]
      }
      return [
        { kind: 'lead', key: 'no-bulk:intro', content: introContent },
        { kind: 'criteria', key: 'no-bulk:criteria', clauses },
        {
          kind: 'result',
          key: 'no-bulk:no-match',
          icon: 'no-match',
          title: <strong>Client Profiles in Zocks and connected records are not applicable for this query.</strong>,
        },
      ]
  }
}
